<template>
  <div class="user-center-page">
    <div class="user-center-layout">
      <!-- 侧边导航栏 -->
      <aside class="sidebar">
        <div class="sidebar-header">
          <el-avatar :size="48" src="https://azlinux.oss-cn-zhangjiakou.aliyuncs.com/sre/media/avatar-default.png" />
          <div class="header-info">
            <span class="user-name">{{ profile.nickname || '未设置昵称' }}</span>
            <el-tag v-if="profile.user_is_vip" type="success" size="small" effect="dark" round>VIP</el-tag>
          </div>
        </div>

        <div class="sidebar-nav">
          <div v-for="item in menuItems" :key="item.id" class="nav-item" :class="{ active: activeTab === item.id }"
            @click="activeTab = item.id">
            <i :class="item.icon"></i>
            <span>{{ item.label }}</span>
          </div>
        </div>

        <div class="sidebar-footer">
          <el-button link type="danger" @click="logout" class="logout-btn">
            <i class="el-icon-switch-button"></i> 退出登录
          </el-button>
        </div>
      </aside>

      <!-- 主内容区 -->
      <main class="main-content">
        <header class="content-header">
          <div class="breadcrumb">
            <span class="root">账户中心</span>
            <span class="separator">/</span>
            <span class="current">{{ currentMenuLabel }}</span>
          </div>

          <div class="header-stats">
            <div class="stat-badge balance" @click="goRecharge">
              <span class="label">可用积分</span>
              <span class="value">{{ balance.points_balance }}</span>
            </div>
            <div class="stat-badge status">
              <span class="label">会员权益</span>
              <span class="value" :class="{ 'vip-text': profile.user_is_vip }">
                {{ profile.user_is_vip ? '有效' : '已到期' }}
              </span>
            </div>
          </div>
        </header>

        <div class="content-body">
          <!-- 概览面板 -->
          <section v-if="activeTab === 'overview'" class="panel overview-panel">
            <el-row :gutter="20">
              <el-col :span="24">
                <el-card class="welcome-card" shadow="never">
                  <h3>欢迎回来，{{ profile.nickname || profile.email }}</h3>
                  <p class="welcome-msg">{{ profile.message || '您的账户运行正常。' }}</p>
                  <div class="quick-actions">
                    <el-button type="primary" size="small" @click="goRecharge">立即充值</el-button>
                    <el-button size="small" @click="activeTab = 'settings'">修改资料</el-button>
                  </div>
                </el-card>
              </el-col>
            </el-row>

            <el-row :gutter="20" style="margin-top: 20px;">
              <el-col :span="12">
                <el-card class="info-card" shadow="never">
                  <template #header>
                    <div class="card-header"><span>账户摘要</span></div>
                  </template>
                  <div class="info-list">
                    <div class="info-item">
                      <span class="label">注册邮箱</span>
                      <span class="value">{{ profile.email }}</span>
                    </div>
                    <div class="info-item">
                      <span class="label">会员到期</span>
                      <span class="value">{{ profile.vip_end_datetime ? formatDate(profile.vip_end_datetime) : '长期有效'
                        }}</span>
                    </div>
                  </div>
                </el-card>
              </el-col>
              <el-col :span="12">
                <el-card class="info-card" shadow="never">
                  <template #header>
                    <div class="card-header"><span>安全状态</span></div>
                  </template>
                  <div class="security-summary">
                    <div class="security-item success">
                      <i class="el-icon-circle-check"></i>
                      <span>登录凭据已安全保存</span>
                    </div>
                    <div class="security-item warning">
                      <i class="el-icon-warning"></i>
                      <span>建议定期修改密码</span>
                    </div>
                  </div>
                </el-card>
              </el-col>
            </el-row>
          </section>

          <!-- 个人资料面板 -->
          <section v-if="activeTab === 'settings'" class="panel settings-panel">
            <el-card shadow="never">
              <template #header>
                <div class="card-header"><span>个人基本资料</span></div>
              </template>
              <el-form label-position="top">
                <el-row :gutter="40">
                  <el-col :span="12">
                    <el-form-item label="运维昵称">
                      <el-input v-model="profile.nickname" placeholder="用于在社区和实验中显示" />
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item label="电子邮件">
                      <el-input v-model="profile.email" disabled />
                    </el-form-item>
                  </el-col>
                </el-row>
                <div class="form-actions">
                  <el-button type="primary" :loading="saving" @click="updateProfile">保存更改</el-button>
                </div>
              </el-form>
            </el-card>
          </section>

          <!-- 积分账户面板 -->
          <section v-if="activeTab === 'billing'" class="panel billing-panel">
            <el-card shadow="never">
              <template #header>
                <div class="card-header"><span>积分管理</span></div>
              </template>
              <div class="billing-grid">
                <div class="balance-display">
                  <div class="label">当前可用余额</div>
                  <div class="amount">{{ balance.points_balance }} <span>Points</span></div>
                  <p class="tip">1 积分 = 0.01 元，云资源使用期间按计费规则持续扣除。</p>
                </div>
                <div class="billing-actions">
                  <el-button type="primary" @click="goRecharge" size="large">充值积分</el-button>
                  <el-button type="success" @click="goConsumption" size="large">消费明细</el-button>
                  <el-button @click="refund" size="large">退款咨询</el-button>
                </div>
              </div>
            </el-card>
          </section>

          <!-- 安全与密码面板 -->
          <section v-if="activeTab === 'security'" class="panel security-panel">
            <el-card shadow="never">
              <template #header>
                <div class="card-header"><span>凭据设置</span></div>
              </template>
              <el-form label-position="top">
                <el-row :gutter="40">
                  <el-col :span="12">
                    <el-form-item label="SRE 实验密码">
                      <el-input v-model="profile.sre_password" :type="passwordType" @input="validatePassword" />
                      <el-button @click="togglePasswordVisibility" type="text" class="eye-btn">
                        {{ passwordType === 'password' ? '显示' : '隐藏' }}
                      </el-button>
                    </el-form-item>
                    <p v-if="passwordError" class="error-text">密码格式不符规则 (字母/数字/@/.)</p>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item label="Root 管理密码">
                      <el-input v-model="profile.root_password" disabled type="password" />
                      <p class="tip-text">Root 密码由系统托管，不可手动修改。</p>
                    </el-form-item>
                  </el-col>
                </el-row>
                <div class="form-actions">
                  <el-button type="primary" :loading="saving" @click="updateProfile">更新安全凭据</el-button>
                </div>
              </el-form>
            </el-card>

            <el-card class="danger-zone" shadow="never" style="margin-top: 40px;">
              <template #header>
                <div class="card-header"><span class="danger-title">危险操作</span></div>
              </template>
              <div class="danger-content">
                <div class="desc">
                  <h4>删除账户</h4>
                  <p>账户注销后，账户资料、积分和实验记录将无法恢复。</p>
                </div>
                <el-button type="danger" plain @click="deleteAccount">销毁账户</el-button>
              </div>
            </el-card>
          </section>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";
import axios from "axios";
import { clearLegacyAuth } from "../services/api";

export default {
  name: "UserCenter",
  data() {
    return {
      activeTab: 'overview',
      menuItems: [
        { id: 'overview', label: '账户概览', icon: 'el-icon-data-board' },
        { id: 'settings', label: '基本设置', icon: 'el-icon-user' },
        { id: 'billing', label: '积分账户', icon: 'el-icon-wallet' },
        { id: 'security', label: '安全凭据', icon: 'el-icon-lock' },
      ],
      passwordError: false,
      saving: false,
      passwordType: "password",
      profile: {
        nickname: "",
        email: "",
        sre_password: "",
        root_password: "",
        password: "",
        message: "",
        user_is_vip: false,
        vip_end_datetime: "",
      },
      balance: { points_balance: 0 },
    };
  },
  computed: {
    currentMenuLabel() {
      const item = this.menuItems.find(i => i.id === this.activeTab);
      return item ? item.label : '';
    }
  },
  mounted() {
    this.initData();
  },
  methods: {
    async initData() {
      try {
        const session = await axios.get("/api/session");
        const realEmail = session.data.email;
        localStorage.setItem("email", "sre-" + realEmail);
        const res = await axios.get(`/api/get_user_info`);
        const d = res.data;
        this.profile = {
          ...this.profile,
          email: realEmail,
          message: d.message,
          vip_end_datetime: d.vip_end_datetime,
          nickname: d.username,
          sre_password: d.sre_password,
          root_password: d.root_password,
          user_is_vip: new Date() <= new Date(d.vip_end_datetime)
        };
        this.loadBalance();
      } catch (e) {
        ElMessage.error("获取用户信息失败");
      }
    },
    async loadBalance() {
      try {
        const res = await axios.get('/api/points/balance');
        this.balance = res.data || { points_balance: 0 };
      } catch (e) { console.error(e); }
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString() : '-';
    },
    validatePassword() {
      this.passwordError = !/^[A-Za-z0-9@.]+$/.test(this.profile.sre_password);
    },
    togglePasswordVisibility() {
      this.passwordType = this.passwordType === "password" ? "text" : "password";
    },
    async updateProfile() {
      this.saving = true;
      try {
        await axios.post(`/api/update_user_info`, {
          username: this.profile.nickname,
          sre_password: this.profile.sre_password,
        });
        ElMessage.success("设置已保存");
      } catch (e) {
        ElMessage.error("保存失败");
      } finally {
        this.saving = false;
      }
    },
    async logout() {
      try {
        await axios.post("/api/logout");
      } finally {
        clearLegacyAuth();
      }
      this.$router.push("/");
    },
    goRecharge() { this.$router.push('/recharge-points'); },
    goConsumption() { this.$router.push('/points-consumption'); },
    refund() { this.$router.push('/support-ticket'); },
    deleteAccount() { ElMessage.info("请提交账号与计费工单，我们将在核验身份后协助处理"); this.$router.push('/support-ticket'); }
  }
};
</script>

<style scoped>
.user-center-page {
  background-color: #f9fafb;
  min-height: calc(100vh - 60px);
  padding: 40px 0;
}

.user-center-layout {
  display: flex;
  max-width: 1200px;
  margin: 0 auto;
  text-align: left;
  background-color: #fff;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

/* 侧边栏样式 - 更加低调专业 */
.sidebar {
  width: 240px;
  background-color: #fcfcfc;
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  padding: 32px 0;
}

.sidebar-header {
  padding: 0 24px 40px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-name {
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
}

.sidebar-nav {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0;
  background: transparent;
  border: none;
  box-shadow: none;
}

.nav-item {
  padding: 12px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  white-space: nowrap; /* 强制文字不换行 */
}

.nav-item i {
  font-size: 16px;
  color: #999;
}

.nav-item:hover {
  background-color: #f5f5f5;
  color: #333;
}

.nav-item.active {
  background-color: #fff;
  color: #111;
  font-weight: 600;
  border-right: 2px solid #333;
  /* 使用低调的深灰色作为激活色 */
}

.nav-item.active i {
  color: #111;
}

.sidebar-footer {
  padding: 16px 24px;
}

.logout-btn {
  font-size: 13px;
  color: #999;
}

.logout-btn:hover {
  color: #f56c6c;
}

/* 主内容区样式 */
.main-content {
  flex: 1;
  padding: 40px 48px;
  background-color: #fff;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.breadcrumb .root {
  color: #9ca3af;
}

.breadcrumb .current {
  color: #111827;
  font-weight: 600;
}

.header-stats {
  display: flex;
  gap: 16px;
}

.stat-badge {
  padding: 8px 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  min-width: 100px;
}

.stat-badge.balance {
  cursor: pointer;
  border-color: #e5e7eb;
  background: #fff;
}

.stat-badge.balance:hover {
  border-color: #d1d5db;
  background: #f9fafb;
}

.stat-badge .label {
  font-size: 11px;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.05em;
}

.stat-badge .value {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.vip-text {
  color: #059669;
}

/* 面板样式 */
.panel {
  max-width: 900px;
}

.el-card {
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  margin-bottom: 24px;
}

.welcome-card {
  background: #fafbfc;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.welcome-card h3 {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.welcome-msg {
  color: #6b7280;
  margin-bottom: 16px;
  font-size: 14px;
}

.card-header {
  font-weight: 600;
  color: #374151;
}

/* 详情列表 */
.info-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid #f3f4f6;
}

.info-item:last-child {
  border-bottom: none;
}

.info-item .label {
  color: #6b7280;
  font-size: 14px;
}

.info-item .value {
  color: #111827;
  font-weight: 500;
  font-size: 14px;
}

.security-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.security-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.security-item.success {
  color: #10b981;
}

.security-item.warning {
  color: #f59e0b;
}

/* 财务网格 */
.billing-grid {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-display .amount {
  font-size: 36px;
  font-weight: 800;
  color: #111827;
  margin: 8px 0;
}

.balance-display .amount span {
  font-size: 16px;
  font-weight: 500;
  color: #6b7280;
}

.balance-display .tip {
  font-size: 13px;
  color: #9ca3af;
}

.billing-actions {
  display: flex;
  gap: 12px;
}

/* 安全面板 */
.eye-btn {
  position: absolute;
  right: 10px;
  top: 0;
}

.tip-text {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
}

.error-text {
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
}

/* 危险区域 */
.danger-zone {
  border: 1px solid #fee2e2;
}

.danger-title {
  color: #ef4444;
}

.danger-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.danger-content h4 {
  margin: 0 0 4px;
  color: #111827;
}

.danger-content p {
  margin: 0;
  color: #6b7280;
  font-size: 13px;
}

/* 响应式 */
@media (max-width: 1024px) {
  .sidebar {
    width: 80px;
  }

  .sidebar-header {
    justify-content: center;
    padding: 0 0 24px;
  }

  .header-info,
  .sidebar-nav span,
  .sidebar-footer span {
    display: none;
  }

  .nav-item {
    justify-content: center;
  }

  .main-content {
    padding: 24px;
  }
}
</style>
